/**
 * An archive's file names (part 17 log, table N): a name is read as the archive holds it, and refused where it could
 * escape a folder, mean something else on another system, or name two files as one. No file system is ever written; the
 * names are what parts 18 and 24 look files up by.
 */
import { ArchiveError } from './problems.ts';

/** N1: the most bytes a name may have. */
export const MAX_NAME_BYTES = 1024;

/** N7: whether `text` holds a C0 or C1 control character or DEL (all in the first plane, so code units suffice). */
function hasControl(text: string): boolean {
  for (const character of text) {
    const unit = character.charCodeAt(0);
    if (unit <= 0x1f || (unit >= 0x7f && unit <= 0x9f)) return true;
  }
  return false;
}

/** N2: names are UTF-8 (ASCII is), whatever flag 11 says; a byte-order mark is kept as a character, not dropped. */
const UTF8 = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });

/** A checked name: its text, and whether it names a directory (ends in `/`, N6). */
export interface EntryName {
  readonly path: string;
  readonly directory: boolean;
}

/** N1–N7: the name `raw` (a central record's bytes) as text, or `bad-name`. */
export function readEntryName(raw: Uint8Array): EntryName {
  if (raw.length > MAX_NAME_BYTES) throw new ArchiveError('bad-name', `a name of ${raw.length} bytes`);
  let path: string;
  try {
    path = UTF8.decode(raw);
  } catch {
    throw new ArchiveError('bad-name', 'a name that is not UTF-8');
  }
  const shown = JSON.stringify(path.slice(0, 200));
  if (path === '') throw new ArchiveError('bad-name', 'an empty name');
  if (path.startsWith('/')) throw new ArchiveError('bad-name', `an absolute name ${shown}`);
  if (path.includes('\\') || path.includes(':')) throw new ArchiveError('bad-name', `a Windows path ${shown}`);
  if (hasControl(path)) throw new ArchiveError('bad-name', `a control character in ${shown}`);
  const directory = path.endsWith('/');
  const segments = (directory ? path.slice(0, -1) : path).split('/');
  if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) throw new ArchiveError('bad-name', `an empty, . or .. segment in ${shown}`);
  return { path, directory };
}

/** N8: what two names are compared by: Unicode's composed form, lower case, without a directory's `/`. */
export function nameKey(name: EntryName): string {
  return (name.directory ? name.path.slice(0, -1) : name.path).normalize('NFC').toLowerCase();
}

/**
 * N8, N9: refuses names equal by {@link nameKey}, and a file's name that another entry uses as a directory. Sorted with
 * `/` read as U+0000 (which no name holds, N7), every key under a folder follows that folder's own key at once, so a
 * file is used as a folder exactly when the next key starts with it and `/`. Each name costs its length and the sort's
 * logarithm, never its depth (deep-fix r1: keeping every folder of every name grew with depth squared, gigabytes for
 * 10 000 deep names).
 */
export function checkNames(names: readonly EntryName[]): void {
  const files = new Map<string, boolean>();
  for (const name of names) {
    const key = nameKey(name).replaceAll('/', '\u0000');
    if (files.has(key)) throw new ArchiveError('bad-name', `two entries named ${JSON.stringify(name.path.slice(0, 200))}`);
    files.set(key, !name.directory);
  }
  const sorted = [...files.keys()].sort();
  for (const [index, key] of sorted.entries()) {
    if (files.get(key) === true && sorted[index + 1]?.startsWith(`${key}\u0000`) === true) {
      throw new ArchiveError('bad-name', `a file used as a folder: ${JSON.stringify(key.replaceAll('\u0000', '/').slice(0, 200))}`);
    }
  }
}
